import { motion } from "framer-motion";
import { Cog, Filter, Flame, Droplets, Search, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { IMAGES } from "@/content/site";

const EQUIPMENT = [
  {
    icon: Cog,
    title: "Pumps & Motors",
    text: "Unusual noise, priming problems, leaks, or a pump that won't start — we diagnose the real cause and repair or replace what's actually needed.",
  },
  {
    icon: Filter,
    title: "Filters",
    text: "Cartridge, DE, and sand filter cleaning, inspection, and repair. A tired filter is the most common reason water won't stay clear.",
  },
  {
    icon: Flame,
    title: "Heaters",
    text: "Heater not firing or taking forever to warm up? We troubleshoot gas and heat-pump systems and give you honest repair-vs-replace guidance.",
  },
  {
    icon: Droplets,
    title: "Salt Chlorine Systems",
    text: "Salt cell cleaning and inspection, control board troubleshooting, and chemistry adjustments that keep salt systems producing properly.",
  },
  {
    icon: Search,
    title: "General Troubleshooting",
    text: "Cloudy water that won't clear, pressure problems, air in the system, mystery leaks at the equipment pad — we find the source and fix it right.",
  },
];

export default function PoolRepairsPage() {
  return (
    <>
      <SEO
        title="Pool Equipment Repair in Pinellas County FL | Point of Pines Pools & Service"
        description="Pool equipment troubleshooting and repairs across Pinellas County, FL — pumps, filters, heaters and salt systems. Honest diagnosis from a local, CPO certified pool company. Call (727) 744-5908."
        path="/pool-repairs/"
      />
      <PageHero
        overline="Pool Equipment & Repairs — Pinellas County, FL"
        title="Pool Equipment,"
        accent="Fixed Honestly"
        description="When your pump, filter, heater, or salt system acts up, you need a straight answer — not an upsell. We troubleshoot pool equipment across Pinellas County and repair what's actually broken."
        image={IMAGES.equipmentAfter}
        imageAlt="Freshly rebuilt pool equipment pad with clean professional plumbing by Point of Pines Pools in Pinellas County"
      />

      <section data-testid="equipment-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">What We Work On</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Diagnosis first. Repair second. Upsell never.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Most equipment problems have a simple cause — and an honest fix. We service the core
              equipment that keeps your pool circulating, filtering, heating, and sanitizing.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {EQUIPMENT.map((e, i) => (
              <motion.div
                key={e.title}
                data-testid={`equipment-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                className="group bg-white rounded-2xl border border-slate-200/80 p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <span className="w-12 h-12 rounded-xl bg-mist flex items-center justify-center transition-colors duration-300 group-hover:bg-navy">
                  <e.icon className="w-6 h-6 text-pool transition-colors duration-300 group-hover:text-sky2" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy tracking-tight">{e.title}</h3>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">{e.text}</p>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.16 }}
              className="bg-navy rounded-2xl p-7 flex flex-col justify-between text-white"
            >
              <p className="font-display text-xl font-semibold tracking-tight leading-snug">
                Not sure what&apos;s wrong? That&apos;s our job to figure out.
              </p>
              <Link
                to="/contact/"
                data-testid="equipment-diagnosis-link"
                className="group mt-6 inline-flex items-center gap-2 font-display font-bold text-aqua hover:text-sky2 transition-colors"
              >
                Request a diagnosis
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="before-after-section" className="py-16 sm:py-24 bg-white border-y border-slate-200/70">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Real Work, Real Results</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Before &amp; after: an equipment pad transformation
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Corroded plumbing and worn-out components out — a clean, professionally plumbed
              system in. This is what &ldquo;repaired the right way&rdquo; actually looks like.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-8">
            <Reveal>
              <figure data-testid="before-photo" className="group">
                <div className="relative clip-arch overflow-hidden ring-1 ring-slate-200 shadow-xl shadow-navy/10">
                  <img
                    src={IMAGES.equipmentBefore}
                    alt="Before: aging pool equipment pad with corroded plumbing and worn filter before service by Point of Pines Pools"
                    className="w-full h-80 sm:h-[26rem] object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <span className="absolute top-4 left-4 bg-navy/85 backdrop-blur-sm text-white text-[11px] font-mono uppercase tracking-[0.2em] px-3.5 py-2 rounded-full">
                    Before
                  </span>
                </div>
              </figure>
            </Reveal>
            <Reveal delay={0.12}>
              <figure data-testid="after-photo" className="group">
                <div className="relative clip-arch overflow-hidden ring-1 ring-slate-200 shadow-xl shadow-navy/10">
                  <img
                    src={IMAGES.equipmentAfter}
                    alt="After: rebuilt pool equipment pad with new filter, salt system and clean professional plumbing by Point of Pines Pools"
                    className="w-full h-80 sm:h-[26rem] object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <span className="absolute top-4 left-4 bg-aqua text-navy text-[11px] font-mono uppercase tracking-[0.2em] font-semibold px-3.5 py-2 rounded-full">
                    After
                  </span>
                </div>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="repair-approach-section" className="py-16 sm:py-24 bg-mist">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Our Approach</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Routine care that prevents repairs
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              The cheapest repair is the one that never happens. Our weekly service customers get
              routine equipment checks and filter care built into every visit — so worn o-rings,
              climbing filter pressure, and early salt cell scale get caught before they sideline
              your pool.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              When something does need repair, we explain what failed, what it takes to fix, and
              whether repair or replacement makes more sense. Then you decide — with real information.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="clip-arch overflow-hidden ring-1 ring-slate-200 shadow-xl shadow-navy/10">
              <img
                src={IMAGES.equipmentVisit}
                alt="Point of Pines Pools service visit with professional equipment at a residential pool in Pinellas County"
                className="w-full h-72 sm:h-96 object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Pool equipment acting up?"
        text="Describe the symptom and we'll schedule a look — anywhere in Pinellas County."
      />
    </>
  );
}
